import React, { useEffect, useRef } from 'react';
import polyline from '@mapbox/polyline';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface RouteMapProps {
  polylineEncoded?: string;
  startLatitude?: number;
  startLongitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  className?: string;
  showMarkers?: boolean;
  showAttribution?: boolean;
}

export default function RouteMap({
  polylineEncoded,
  startLatitude,
  startLongitude,
  endLatitude,
  endLongitude,
  className = 'h-64 w-full',
  showMarkers = true,
  showAttribution = true,
}: RouteMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up existing map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
    }

    // Check if we have any route data
    if (!polylineEncoded && (!startLatitude || !startLongitude)) {
      return;
    }

    // Initialize map
    const map = L.map(mapRef.current, {
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: showAttribution,
      maxZoom: 18,
    });

    mapInstanceRef.current = map;

    // Add light grey tile layer to match Races page
    L.tileLayer(
      `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(import.meta.env.OPENMAPS_API_KEY || '')}`,
      {
        attribution: showAttribution
          ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
          : '',
      },
    ).addTo(map);

    // Custom marker icons - small dots without letters
    const startIcon = L.divIcon({
      html: '<div style="background-color: #10b981; border-radius: 50%; width: 10px; height: 10px; border: 1px solid white; box-shadow: 0 1px 2px rgba(0,0,0,0.3);"></div>',
      className: 'custom-div-icon',
      iconSize: [8, 8],
      iconAnchor: [4, 4],
    });

    const endIcon = L.divIcon({
      html: '<div style="background-color: #ef4444; border-radius: 50%; width: 10px; height: 10px; border: 1px solid white; box-shadow: 0 1px 2px rgba(0,0,0,0.3);"></div>',
      className: 'custom-div-icon',
      iconSize: [8, 8],
      iconAnchor: [4, 4],
    });

    try {
      if (polylineEncoded) {
        // Decode polyline and draw route
        const coordinates: [number, number][] =
          polyline.decode(polylineEncoded);

        if (coordinates.length > 0) {
          // Convert to Leaflet format [lat, lng]
          const latlngs: [number, number][] = coordinates.map((coord) => [
            coord[0],
            coord[1],
          ]);

          // Draw the route with a high-visibility athletic brand accent.
          const routeLine = L.polyline(latlngs, {
            color: '#C4FF00',
            weight: 3,
            opacity: 0.9,
            smoothFactor: 1,
          }).addTo(map);

          if (showMarkers) {
            // Add start marker
            const startPoint = latlngs[0];
            L.marker(startPoint, { icon: startIcon })
              .addTo(map)
              .bindPopup('Start');

            // Add end marker
            const endPoint = latlngs[latlngs.length - 1];
            L.marker(endPoint, { icon: endIcon })
              .addTo(map)
              .bindPopup('Finish');
          }

          // Fit map to route bounds
          map.fitBounds(routeLine.getBounds(), {
            padding: showMarkers ? [10, 10] : [0, 0],
          });
        }
      } else if (startLatitude && startLongitude) {
        // Show just start/end points if no polyline
        const startPoint: [number, number] = [startLatitude, startLongitude];

        if (showMarkers) {
          L.marker(startPoint, { icon: startIcon })
            .addTo(map)
            .bindPopup('Start');
        }

        if (endLatitude && endLongitude) {
          const endPoint: [number, number] = [endLatitude, endLongitude];
          if (showMarkers) {
            L.marker(endPoint, { icon: endIcon })
              .addTo(map)
              .bindPopup('Finish');
          }

          // Fit bounds to both points
          const bounds = L.latLngBounds([startPoint, endPoint]);
          map.fitBounds(bounds, { padding: showMarkers ? [20, 20] : [2, 2] });
        } else {
          // Center on start point
          map.setView(startPoint, 15);
        }
      }
    } catch (error) {
      console.error('Error rendering route map:', error);

      // Fallback: show a default view if we have coordinates
      if (startLatitude && startLongitude) {
        map.setView([startLatitude, startLongitude], 13);
        if (showMarkers) {
          L.marker([startLatitude, startLongitude])
            .addTo(map)
            .bindPopup('Workout Location');
        }
      }
    }

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [
    polylineEncoded,
    startLatitude,
    startLongitude,
    endLatitude,
    endLongitude,
    showMarkers,
    showAttribution,
  ]);

  // Don't render if no route data
  if (!polylineEncoded && (!startLatitude || !startLongitude)) {
    return null;
  }

  return (
    <div className={className}>
      <div ref={mapRef} className="w-full h-full rounded-lg overflow-hidden" />
    </div>
  );
}
